import React, { useRef, useState } from 'react';
import { Camera, FileText, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { orderFileUrl, wdel, wupload } from './api';

/** Multi-file upload for one category on a website order (works from phone camera roll). */
export function FileUploader({ order, category, label, hint, optional, onChange, readOnly }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(0);
  const files = (order?.files || []).filter((f) => f.category === category);

  const pick = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    if (!list.length) return;
    setBusy(list.length);
    for (const file of list) {
      try {
        const rec = await wupload(`/website/orders/${order.id}/files?category=${category}`, file);
        onChange?.((cur) => ({ ...cur, files: [...(cur.files || []), rec] }));
      } catch (err) { toast.error(`${file.name}: ${err.message}`); }
      setBusy((b) => b - 1);
    }
  };
  const remove = async (f) => {
    try { await wdel(`/website/orders/${order.id}/files/${f.id}`); onChange?.((cur) => ({ ...cur, files: (cur.files || []).filter((x) => x.id !== f.id) })); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <div data-testid={`ww-files-${category}`}>
      <div className="flex items-baseline justify-between"><span className="text-sm font-semibold text-slate-800">{label}{optional && <span className="text-slate-400 font-normal"> (optional)</span>}</span><span className="text-xs text-slate-400">{files.length} file{files.length === 1 ? '' : 's'}</span></div>
      {hint && <p className="text-xs text-slate-500 mb-2">{hint}</p>}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-1">
        {files.map((f) => (
          <div key={f.id} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50 group" data-testid={`ww-file-${f.id}`}>
            <a href={orderFileUrl(order.id, f.id)} target="_blank" rel="noreferrer" className="block w-full h-full">
              {f.content_type?.startsWith('image/') ? <img src={orderFileUrl(order.id, f.id)} alt={f.filename} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 p-1"><FileText className="w-6 h-6" /><span className="text-[10px] truncate w-full text-center">{f.filename}</span></div>}
            </a>
            {!readOnly && <button type="button" onClick={() => remove(f)} aria-label="Remove file" data-testid={`ww-file-remove-${f.id}`} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-rose-600 flex items-center justify-center shadow"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
        {!readOnly && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy > 0} data-testid={`ww-files-add-${category}`}
            className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-teal-600 text-slate-500 hover:text-teal-700 flex flex-col items-center justify-center gap-1 text-xs font-semibold disabled:opacity-60">
            {busy > 0 ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}{busy > 0 ? `Uploading ${busy}…` : 'Add'}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" multiple accept={category === 'branding' ? undefined : 'image/*,.pdf,.svg'} className="hidden" onChange={pick} data-testid={`ww-files-input-${category}`} />
    </div>
  );
}
